import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {Box, Image, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import CoverSwatchImageNotAvailable from '@amblique/king-living/app/components/cover-swatch-image-not-available';
import {PIM_SQUARE_THUMBNAIL_PX} from '@amblique/king-living/app/components/configuration-selector/utils';

import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {getCoverExtraPrice, getCoverStockMessageClassName, getCoverStockMessageStyleKey, getStockLevel} from '@amblique/king-living/app/utils/cover-stock-display';
import {handleRadioTileKeyDown} from '@amblique/king-living/app/utils/radio-keyboard';
import {sfccProductIdDataAttrs} from '@amblique/king-living/app/utils/product-utils';
import {getViewDetailsProductTitle} from '@amblique/king-living/app/utils/utils';

/**
 * Combined cover heading: `materialName + productName`.
 * Skip concat only when the two strings are identical (dev catalog duplicate).
 *
 * @param {string} materialName
 * @param {string} productName
 * @returns {string}
 */
function joinCoverDisplayName(materialName, productName) {
  if (!materialName) return productName || '';
  if (!productName) return materialName;
  if (productName === materialName) return materialName;

  return `${materialName} ${productName}`;
}

function getCoverMaterialName(variant) {
  const processed = variant?.c_processedData ?? {};

  return (processed.materialName ?? variant?.c_materialName ?? '').trim();
}

function getCoverProductName(variant) {
  const processed = variant?.c_processedData ?? {};

  return (
    variant?.productName ??
    processed.productName ??
    variant?.name ??
    ''
  ).trim();
}

/**
 * Cover tile labels (DE-268 / Vasyl):
 * - line1: PIM collection (`c_processedData.materialName`, e.g. "Origin")
 * - line2: catalog cover / variant name (`productName` / `name`, e.g. "Esperance Sand")
 * Combined (aria / View Details heading): `materialName + productName`.
 *
 * @param {Object} variant - `c_allVariantsData` entry or SCAPI variant
 * @returns {{ line1: string|null, line2: string|null, ariaLabel: string }}
 */
export function getCoverVariantLabelLines(variant) {
  const materialName = getCoverMaterialName(variant);
  const productName = getCoverProductName(variant);

  const line1 = materialName || null;
  let line2 = productName || null;

  if (line1 && line2 && line2 === line1) {
    line2 = null;
  }

  // No material name — keep a single primary line so the tile still labels the cover.
  if (!line1 && line2) {
    return {
      ariaLabel: line2,
      line1: line2,
      line2: null,
    };
  }

  const ariaLabel = joinCoverDisplayName(line1 || '', line2 || '');

  return {line1, line2, ariaLabel};
}

/**
 * View Details heading for a cover: `materialName + productName`.
 * Returns '' when `materialName` is missing so callers can fall back to
 * {@link getViewDetailsProductTitle} (option SKUs).
 *
 * @param {Object} [product] - Cover variant or merged View Details product
 * @param {Object} [masterProduct]
 * @returns {string}
 */
export function getCoverViewDetailsTitle(product, masterProduct) {
  const materialName = getCoverMaterialName(product);

  if (!materialName) return '';

  const rawProductName = getCoverProductName(product);
  const strippedProductName = getViewDetailsProductTitle(
    {id: product?.id ?? product?.productId, name: rawProductName},
    masterProduct
  ) || rawProductName;

  return joinCoverDisplayName(materialName, strippedProductName);
}

/**
 * Cover variant tile component for the cover step.
 * Displays a horizontal card with:
 * - Rectangle thumbnail (76×60px)
 * - name1: PIM materialName (collection); name2: catalog cover / productName
 * - Badge: badgeText (if present)
 * - Extra price: variant price - master min price (when > 0)
 * - "View detail" link (mobile only)
 *
 * @param {Object} props.variant - Variant with c_processedData.materialName, productName/name, salePrice, swatchImageUrl
 * @param {boolean} props.isSelected - Whether this variant is selected
 * @param {boolean} props.isMobile - Whether on mobile (shows "View detail" link)
 * @param {string} [props.currency] - Currency code for extra price display
 * @param {number} [props.masterMinPrice] - Master product min price; variant extra price = variant.salePrice - masterMinPrice
 * @param {Object} props.styles - Theme styles
 * @param {Function} props.onSelect - Selection handler
 * @param {Function} [props.onViewDetails] - View details handler (optional, mobile only)
 */
export function CoverVariantTile({
  currency,
  hideStockLabels = false,
  isMobile,
  isSelected,
  masterMinPrice,
  styles,
  tabIndex = 0,
  useSimplePdpCoverStyles = false,
  variant,
  onSelect,
  onViewDetails,
}) {
  const intl = useIntl();
  const currencyCode = currency ?? 'USD';
  const {
    line1: name1,
    line2: name2,
    ariaLabel: coverAriaLabel,
  } = getCoverVariantLabelLines(variant);
  const badgeText = variant.c_processedData?.badgeText ?? '';
  const imageUrl = variant.swatchImageUrl;

  // Simple PDP cover flyout ("+N more") must match CoverSwatchGrid: tracked stock shows
  // In stock / Low stock / Sold out; MTO/perpetual (or Family B hideStockLabels) shows the
  // cover price rule and keeps tiles selectable. Complex Customize keeps price-only layout.
  const stockLevel = useSimplePdpCoverStyles ?
    (hideStockLabels ? 'perpetual' : getStockLevel(variant)) :
    null;
  const isPerpetual = stockLevel === 'perpetual';
  const isOutOfStock = stockLevel === 'oos';
  const gridStockStyles = useMultiStyleConfig('CoverSwatchGrid', {isSelected, isOutOfStock});

  let simplePdpStockMessage = null;
  let simplePdpStockMessageStyle = null;
  let simplePdpStockMessageClassName = null;
  let simplePdpStockBadge = null;

  if (useSimplePdpCoverStyles) {
    simplePdpStockMessageStyle = gridStockStyles[getCoverStockMessageStyleKey(stockLevel ?? 'perpetual')];
    simplePdpStockMessageClassName = getCoverStockMessageClassName(stockLevel ?? 'perpetual', {
      variant: 'flyout',
    });

    if (isPerpetual) {
      const coverExtraPrice = getCoverExtraPrice(variant, masterMinPrice);

      simplePdpStockMessage = coverExtraPrice != null ?
        `+ ${formatCurrencyHideTrailingZeroCents(intl, coverExtraPrice, currencyCode)}` :
        intl.formatMessage({defaultMessage: 'Included', id: 'cover_swatch_grid.price.included'});

      if (coverExtraPrice == null) {
        simplePdpStockMessageClassName += ' cover-variant-tile-content-stock-message--included';
      }
    } else if (stockLevel === 'in-stock') {
      simplePdpStockMessage = intl.formatMessage({
        defaultMessage: 'Available',
        id: 'cover_swatch_grid.stock.available',
      });
      simplePdpStockBadge = (
        <Text {...gridStockStyles.tileStockBadge} as="span">
          <FormattedMessage defaultMessage="In stock" id="cover_swatch_grid.badge.in_stock" />
        </Text>
      );
    } else if (stockLevel === 'low-stock') {
      simplePdpStockMessage = intl.formatMessage(
        {
          defaultMessage: 'Only {count, plural, one {# left} other {# left}}',
          id: 'cover_swatch_grid.stock.only_n_left',
        },
        {count: variant.ats ?? 0}
      );
      simplePdpStockBadge = (
        <Text {...gridStockStyles.tileStockBadgeLow} as="span">
          <FormattedMessage defaultMessage="Low stock" id="cover_swatch_grid.badge.low_stock" />
        </Text>
      );
    } else {
      simplePdpStockMessage = intl.formatMessage({
        defaultMessage: 'Sold out',
        id: 'cover_swatch_grid.stock.sold_out',
      });
    }
  }

  // #2 Variant extra price: in each group, compare this variant's price with master min price
  const variantExtraPrice =
    masterMinPrice != null &&
    variant.salePrice != null &&
    variant.salePrice > 0 &&
    (variant.salePrice - masterMinPrice) > 0 ?
      variant.salePrice - masterMinPrice :
      null;

  const handleViewDetails = (e) => {
    e.stopPropagation();
    onViewDetails?.(variant);
  };

  const cardStyles = isSelected ?
    {...styles.coverVariantTile, ...styles.coverVariantTileSelected} :
    styles.coverVariantTile;
  const isDisabled = useSimplePdpCoverStyles && isOutOfStock;
  const handleSelect = () => onSelect(isSelected ? null : variant);
  const imageWrapperStyles = useSimplePdpCoverStyles ?
    styles.coverVariantTileImageWrapperSimplePdp :
    styles.coverVariantTileImageWrapper;
  const badgeStyles = useSimplePdpCoverStyles ?
    styles.coverVariantTileBadgeSimplePdp :
    styles.coverVariantTileBadge;

  return (
    <Box
      {...cardStyles}
      aria-checked={isSelected}
      aria-disabled={isDisabled || undefined}
      aria-label={coverAriaLabel}
      className="cover-variant-tile"
      {...sfccProductIdDataAttrs('variant', variant)}
      role="radio"
      tabIndex={isDisabled ? -1 : tabIndex}
      onClick={() => !isDisabled && handleSelect()}
      onKeyDown={(event) => handleRadioTileKeyDown(event, isDisabled, handleSelect)}
    >
      {/* Thumbnail */}
      <Box
        className="cover-variant-tile-image-wrapper"
        {...imageWrapperStyles}
      >
        {imageUrl ? (
          <Image
            alt={coverAriaLabel}
            htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
            htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
            loading="lazy"
            objectFit="contain"
            src={imageUrl}
            {...styles.coverVariantTileImage}
          />
        ) : (
          <CoverSwatchImageNotAvailable
            svgProps={styles.coverVariantTileImage}
            wrapperProps={imageWrapperStyles}
          />
        )}
        {badgeText ? (
          <Text className="cover-variant-tile-badge" {...badgeStyles}>
            {badgeText}
          </Text>
        ) : (
          simplePdpStockBadge
        )}
        {useSimplePdpCoverStyles && isOutOfStock && (
          <Box
            className="cover-swatch-grid__tile-oos-overlay-line"
            {...gridStockStyles.tileOOSOverlayLine}
          />
        )}
      </Box>

      <Box className="cover-variant-tile-content" {...styles.coverVariantTileContent}>
        {name1 && (
          <Text className="cover-variant-tile-content-name1" noOfLines={1} {...styles.coverVariantTileName1}>
            {name1}
          </Text>
        )}
        {name2 && (
          <Text className="cover-variant-tile-content-name2" noOfLines={2} {...styles.coverVariantTileName2}>
            {name2}
          </Text>
        )}

        {/*
          Simple PDP cover flyout: single stock-or-price line that matches the inline grid
          (Available / Only N left / Sold out for tracked stock, "+ delta" / "Included" for
          made-to-order). Complex "Customize Your Own" keeps its extra-price + dual-price layout.
        */}
        {useSimplePdpCoverStyles ? (
          simplePdpStockMessage && (
            <Text
              className={simplePdpStockMessageClassName}
              {...simplePdpStockMessageStyle}
            >
              {simplePdpStockMessage}
            </Text>
          )
        ) : (
          <>
            {/* Variant extra price: variant.salePrice - masterMinPrice (when > 0) */}
            {variantExtraPrice != null && variantExtraPrice > 0 && (
              <Text className="cover-variant-tile-extra-price" color={{base: 'brandColors.greys', lg: 'greyscale90Alpha.50'}} fontSize={{base: '13px', lg: '12px'}} marginTop="6px">
                +{' '}
                {formatCurrencyHideTrailingZeroCents(intl, variantExtraPrice, currencyCode)}
              </Text>
            )}

            {/* Dual price: sale + list (strikethrough) when variant has a list price */}
            {variant.price != null && variant.salePrice != null && variant.price > variant.salePrice && (
              <Text className="cover-variant-tile-dual-price" color={{base: 'brandColors.greys', lg: 'greyscale90Alpha.50'}} fontSize={{base: '13px', lg: '12px'}} marginTop="6px">
                {formatCurrencyHideTrailingZeroCents(intl, variant.salePrice, currencyCode)}
                {' '}
                <Text as="span" color="greyscale.70" fontSize="xs" textDecoration="line-through">
                  {formatCurrencyHideTrailingZeroCents(intl, variant.price, currencyCode)}
                </Text>
              </Text>
            )}
          </>
        )}

        {isMobile && onViewDetails && (
          <Text
            className="view-detail-link"
            {...styles.coverVariantTileViewDetails}
            as="span"
            display={{base: 'inline', lg: 'none'}}
            tabIndex={-1}
            onClick={handleViewDetails}
          >
            <FormattedMessage
              defaultMessage="See details"
              id="customize_panel.cover_variant.view_details"
            />
          </Text>
        )}
      </Box>
    </Box>
  );
}

CoverVariantTile.displayName = 'CoverVariantTile';

CoverVariantTile.propTypes = {
  currency: PropTypes.string,
  hideStockLabels: PropTypes.bool,
  isMobile: PropTypes.bool,
  isSelected: PropTypes.bool,
  masterMinPrice: PropTypes.number,
  tabIndex: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func,
  styles: PropTypes.object.isRequired,
  useSimplePdpCoverStyles: PropTypes.bool,
  variant: PropTypes.shape({
    productId: PropTypes.string,
    name: PropTypes.string,
    productName: PropTypes.string,
    ats: PropTypes.number,
    price: PropTypes.number,
    salePrice: PropTypes.number,
    swatchName: PropTypes.string,
    swatchImageUrl: PropTypes.string,
    variationValues: PropTypes.object,
    c_processedData: PropTypes.shape({
      badgeText: PropTypes.string,
      materialName: PropTypes.string,
      productName: PropTypes.string,
    }),
  }).isRequired,
};
